import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Empty } from "@/components/kit";
import { useAuth, isAdmin } from "@/context/AuthContext";
import useLoad from "@/lib/useLoad";
import api, { errMsg } from "@/lib/api";
import { MediaThumb, MediaPlayer } from "./ExerciseMedia";
import ExerciseDialog from "./ExerciseDialog";

function ExerciseCard({ ex, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(ex)} className="panel rounded-lg p-1.5 text-left flex flex-col gap-1.5 fade-up hover:border-[#FF3B30]/60 transition-colors" data-testid={`exercise-card-${ex.id}`}>
      <MediaThumb media={ex.media} imageUrl={ex.image_url} />
      <div>
        <div className="font-display text-lg font-semibold uppercase leading-tight">{ex.name}</div>
      </div>
    </button>
  );
}

function Viewer({ ex, canEdit, onClose, onEdit, onDelete }) {
  return (
    <Dialog open={!!ex} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-[#0B1220] border-[#1E293B]" data-testid="exercise-viewer">
        {ex && (<>
          <DialogHeader><DialogTitle className="font-display uppercase text-2xl" data-testid="exercise-viewer-title">{ex.name}</DialogTitle></DialogHeader>
          <MediaPlayer media={ex.media} imageUrl={ex.image_url} />
          <div className="text-xs text-slate-300">Primary muscle: {ex.primary_muscle || ex.muscle_group || "Not specified"}</div>
          {!!ex.secondary_muscles?.length && <div className="text-xs text-slate-400">Secondary muscles: {ex.secondary_muscles.map((item) => item.name).join(" · ")}</div>}
          {!!ex.equipment_items?.length && <div className="text-xs text-slate-400">Equipment: {ex.equipment_items.map((item) => item.name).join(" · ")}</div>}
          {!!ex.exercise_categories?.length && <div className="text-xs text-slate-400">Categories: {ex.exercise_categories.map((item) => item.name).join(" · ")}</div>}
          {ex.coach_name && <div className="text-xs text-slate-500">Added by {ex.coach_name}</div>}
          {ex.instructions && <p className="text-sm text-slate-300 whitespace-pre-wrap" data-testid="exercise-viewer-instructions">{ex.instructions}</p>}
          {canEdit && (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onDelete(ex)} data-testid="exercise-delete-btn"><Trash2 size={14} className="mr-1" />Delete</Button>
              <Button variant="outline" onClick={() => onEdit(ex)} data-testid="exercise-edit-btn"><Pencil size={14} className="mr-1" />Edit</Button>
            </div>
          )}
        </>)}
      </DialogContent>
    </Dialog>
  );
}

export default function ExercisesTab() {
  const { user } = useAuth();
  const [items, reload] = useLoad("/exercises");
  const [muscles] = useLoad("/muscle-types");
  const [equipmentOptions] = useLoad("/equipment");
  const [categoryOptions] = useLoad("/exercise-categories");
  const [q, setQ] = useState("");
  const [primaryFilter, setPrimaryFilter] = useState("all");
  const [secondaryFilter, setSecondaryFilter] = useState("all");
  const [equipmentFilter, setEquipmentFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(undefined);

  const shown = useMemo(() => (items || []).filter((e) => {
    const searchText = [e.name, e.primary_muscle, e.muscle_group, e.equipment,
      ...(e.secondary_muscles || []).map((item) => item.name),
      ...(e.equipment_items || []).map((item) => item.name),
      ...(e.exercise_categories || []).map((item) => item.name)].join(" ").toLowerCase();
    return searchText.includes(q.toLowerCase())
      && (primaryFilter === "all" || e.primary_muscle_id === primaryFilter)
      && (secondaryFilter === "all" || (e.secondary_muscle_ids || []).includes(secondaryFilter))
      && (equipmentFilter === "all" || (e.equipment_ids || []).includes(equipmentFilter))
      && (categoryFilter === "all" || (e.category_ids || []).includes(categoryFilter));
  }), [items, q, primaryFilter, secondaryFilter, equipmentFilter, categoryFilter]);
  const canEdit = (ex) => isAdmin(user) || ex.coach_id === user?.id;

  const remove = async (ex) => {
    if (!window.confirm(`Delete "${ex.name}"?`)) return;
    try { await api.delete(`/exercises/${ex.id}`); toast.success("Exercise deleted"); setViewing(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input className="pl-8" placeholder="Search exercises, muscles, equipment" value={q} onChange={(e) => setQ(e.target.value)} data-testid="exercise-search-input" />
        </div>
        <Button onClick={() => setEditing(null)} data-testid="add-exercise-btn"><Plus size={15} className="mr-1" />Add exercise</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[["Primary muscle", primaryFilter, setPrimaryFilter, muscles], ["Secondary muscle", secondaryFilter, setSecondaryFilter, muscles],
          ["Equipment", equipmentFilter, setEquipmentFilter, equipmentOptions], ["Category", categoryFilter, setCategoryFilter, categoryOptions]].map(([label, value, setValue, options]) => (
          <div key={label}>
            <Label className="text-xs text-slate-500">{label}</Label>
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger className="mt-1" data-testid={`exercise-filter-${label.toLowerCase().replaceAll(" ", "-")}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All {label.toLowerCase()}s</SelectItem>
                {(options || []).map((option) => <SelectItem key={option.id} value={option.id}>{option.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      {!items ? <div className="eyebrow">Loading…</div> : !shown.length ? <Empty text={q ? "No exercises match your search." : "No exercises yet. Add your first one."} testid="exercises-empty" /> : (
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3" data-testid="exercise-grid">
          {shown.map((ex) => <ExerciseCard key={ex.id} ex={ex} onOpen={setViewing} />)}
        </div>
      )}
      <Viewer ex={viewing} canEdit={viewing && canEdit(viewing)} onClose={() => setViewing(null)}
        onEdit={(ex) => { setViewing(null); setEditing(ex); }} onDelete={remove} />
      <ExerciseDialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)} exercise={editing}
        onSaved={() => { setEditing(undefined); reload(); }} />
    </div>
  );
}
